import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  UserImportDataSource,
  UserImportMapping,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import {
  Button,
  DialogActions,
  DialogContent,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@material-ui/core';
import { isEqual } from 'lodash';
import { ExternalLink, Link } from 'lucide-react';
import { useState } from 'react';
import { DateFormatPicker } from '../../../../../../components/DateFormatPicker';
import { InfoBox } from '../../../../../../components/InfoBox';
import { useSnackbar } from '../../../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { FlexContainer } from '../../../../../../layout/Flex';
import { useRoutes } from '../../../../../../routes';
import { removeDoc, updateDoc } from '../../../../../../services/firebase';
import { CustomImportMultiMappingForm2 } from '../CustomImportMappingForm';

type CustomMappings = {
  customMappings: UserImportMapping[];
};

type Mappings = CustomMappings & {
  inferredOtherUsers: CustomMappings[];
};

const getUserImportMappingsWithoutEmpty = (
  ms: UserImportMapping[]
): UserImportMapping[] => ms.filter((m) => m.dadoKey && m.otherKey);

export const getMappingsWithoutEmpty = ({
  customMappings,
  inferredOtherUsers
}: Mappings): Mappings => ({
  customMappings: getUserImportMappingsWithoutEmpty(customMappings),
  inferredOtherUsers: Array.isArray(inferredOtherUsers)
    ? inferredOtherUsers
        .map((ms) => ({
          customMappings: getUserImportMappingsWithoutEmpty(ms.customMappings)
        }))
        .filter((ms) => ms.customMappings.length > 0)
    : []
});

export const Img = styled('img')<{ maxHeight?: number }>((p) => ({
  width: 'auto',
  height: 'auto',
  maxHeight: p.maxHeight ? p.maxHeight : 64,
  maxWidth: '80%',
  marginBottom: p.theme.spacing(2)
}));

export const ImportSourceListItem = ({
  enabled,
  logoUrl,
  name,
  viewButton
}: {
  enabled: boolean;
  logoUrl: string;
  name: string;
  viewButton?: {
    href: string;
    disabled?: boolean;
  };
}) => {
  return (
    <ListItem divider disableGutters>
      <ListItemIcon>
        <img src={logoUrl} height={16} alt={name} />
      </ListItemIcon>
      <ListItemText
        primary={
          <FlexContainer>
            {viewButton ? (
              <Button
                size="small"
                href={viewButton.href}
                rel="noopener"
                target="_blank"
                endIcon={<ExternalLink size={14} />}
              >
                {name}
              </Button>
            ) : (
              <div>{name}</div>
            )}
          </FlexContainer>
        }
      />
    </ListItem>
  );
};

const SOURCES_WITH_DATE_FORMAT_PICKER = [
  UserImportSourceType.GOOGLE_SHEET,
  UserImportSourceType.MS_EXCEL,
  UserImportSourceType.WORKDAY_REPORT_API,
  UserImportSourceType.SFTP,
  UserImportSourceType.MANUAL_CSV,
  UserImportSourceType.AIRTABLE
];

export const ImportMappingForm = ({
  d,
  fieldTerm,
  integrationName,
  userFields
}: {
  d: Doc<UserImportDataSource>;
  fieldTerm?: string;
  integrationName: string;
  userFields: CustomUserField[];
}) => {
  const originalDateFormats =
    (d.data as { dateFormats?: string[] }).dateFormats ?? [];

  const [state, setState] = useState<
    UserImportDataSource & { dateFormats?: string[] }
  >({
    ...d.data,
    dateFormats: originalDateFormats
  });
  const { enqueueSnackbarSuccess } = useSnackbar();
  const { t } = useUnsafeTranslation();

  const showDateFormatPicker = SOURCES_WITH_DATE_FORMAT_PICKER.includes(
    d.data.type
  );

  const isDataChanged = () =>
    !isEqual(d.data.customMappings, state.customMappings) ||
    !isEqual(d.data.inferredOtherUsers, state.inferredOtherUsers) ||
    !isEqual(originalDateFormats, state.dateFormats ?? []);

  return (
    <>
      {showDateFormatPicker && (
        <div style={{ marginBottom: 16 }}>
          <DateFormatPicker
            value={state.dateFormats || []}
            onChange={(formats) =>
              setState((prevState) => ({
                ...prevState,
                dateFormats: formats
              }))
            }
          />
        </div>
      )}
      <CustomImportMultiMappingForm2
        additionalCompositeFields={
          d.data.type === UserImportSourceType.WORKDAY_REPORT_API
            ? ['$FULL_NAME$', '$FULL_NAME_LN_FN$']
            : ['$FULL_NAME$']
        }
        enableSave={isDataChanged()}
        fieldTerm={fieldTerm}
        integrationName={
          d.data.type === UserImportSourceType.SFTP
            ? t('your CSV file')
            : integrationName
        }
        userFields={userFields}
        value={{
          main: state.customMappings,
          others: state.inferredOtherUsers?.map((v) => v.customMappings)
        }}
        onChange={(mappings) =>
          setState((prevState) => ({
            ...prevState,
            customMappings: mappings.main,
            inferredOtherUsers: mappings.others?.map((v) => ({
              customMappings: v
            }))
          }))
        }
        onDelete={() => removeDoc(d)}
        onSave={async () => {
          await updateDoc(d, () => ({
            ...getMappingsWithoutEmpty({
              customMappings: state.customMappings,
              inferredOtherUsers: state.inferredOtherUsers || []
            }),
            dateFormats: state.dateFormats
          }));

          enqueueSnackbarSuccess(t('Mapping saved!'));
        }}
      />
    </>
  );
};

export const IntegrationNotConfigured = ({
  integration,
  onClose
}: {
  integration: string;
  onClose?: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { ROUTES } = useRoutes();
  return (
    <>
      <DialogContent>
        <InfoBox>{t('import.notSetup', { integration: integration })}</InfoBox>
      </DialogContent>
      <DialogActions>
        {onClose && <Button onClick={onClose}>{t('Cancel')}</Button>}
        <Link to={ROUTES.org.integrations.url()}>
          <Button color="primary" variant="contained">
            {t('Set up integrations')}
          </Button>
        </Link>
      </DialogActions>
    </>
  );
};
